import { useEffect } from 'react';

// tabindex="-1" отбрасываем везде: так же поступает и сам браузер, обходя страницу табом.
// Иначе в список попадают служебные кнопки «Dismiss», которые HeroUI ставит по краям окна.
const FOCUSABLE = [
  'a[href]',
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]',
]
  .map((selector) => `${selector}:not([tabindex="-1"])`)
  .join(',');

// Тост у HeroUI — тоже alertdialog, но с aria-modal="false": это сообщение, а не окно.
const DIALOG =
  '[role="dialog"]:not([aria-modal="false"]),[role="alertdialog"]:not([aria-modal="false"])';

const visible = (el: HTMLElement) => el.offsetParent !== null || el.getClientRects().length > 0;

/**
 * Перевод фокуса в открывшееся окно.
 *
 * Удержание и возврат фокуса HeroUI делает сама: её окна и поповеры рендерятся через
 * `Overlay` из `@react-aria/overlays`, а он оборачивает содержимое в `FocusScope`
 * с `restoreFocus: true` и `contain`. Чего там нет — это `autoFocus`, поэтому фокус
 * в окно не переходит и остаётся на кнопке, которая его открыла. А кнопка на это время
 * уже спрятана от скринридера: `#root` получает `aria-hidden="true"`.
 *
 * Проверено на кабинете: при открытом окне фокус оставался на «Reissue link» позади него.
 *
 * Компонент библиотеки не трогаем, недостающее добавляем снаружи. Окно ищем по роли:
 * `ModalContent` не forwardRef, ссылку внутрь не передать.
 */
export const useModalAutoFocus = (isOpen: boolean): void => {
  useEffect(() => {
    if (!isOpen) return undefined;

    // Окно въезжает анимацией: на первом кадре его ещё нет в разметке. Пробуем несколько раз.
    let attempt = 0;
    let timer = 0;

    const focusInside = () => {
      const all = document.querySelectorAll<HTMLElement>(DIALOG);
      const dialog = all.length ? all[all.length - 1] : null;
      const [first] = dialog
        ? [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(visible)
        : [];

      if (first) {
        first.focus();
        return;
      }

      // Окно без единой кнопки — фокус ставим на него само, иначе он останется снаружи
      if (dialog && attempt >= 3) {
        dialog.tabIndex = -1;
        dialog.focus();
        return;
      }

      attempt += 1;
      if (attempt <= 8) timer = window.setTimeout(focusInside, 60);
    };

    timer = window.setTimeout(focusInside, 0);

    return () => window.clearTimeout(timer);
  }, [isOpen]);
};
